import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { sdk } from '@farcaster/miniapp-sdk';
import { FarcasterUser } from '../types/farcaster';
import { createOrUpdateUser, checkReadingLimits } from '../utils/supabaseUtils';

interface QuickAuthSession {
  fid: number;
  expiresAt: string | null;
  notifyEnabled: boolean;
}

const MOCK_USER: FarcasterUser = {
  fid: 999999,
  username: 'localtester',
  display_name: 'Local Tester',
  pfp_url: '',
  profile: { bio: { text: 'Local development mode' } },
  follower_count: 0,
  following_count: 0,
};

const allowBrowserAccess = import.meta.env.VITE_ALLOW_BROWSER_ACCESS === 'true';
if (import.meta.env.DEV) {
  console.log('[env] allowBrowserAccess =', import.meta.env.VITE_ALLOW_BROWSER_ACCESS);
}
const AUTH_MODE = import.meta.env.VITE_AUTH_MODE ?? 'quick';

export const useMiniAppAuth = () => {
  const [user, setUser] = useState<FarcasterUser | null>(null);
  const [dbUser, setDbUser] = useState<any | null>(null);
  const [dailyLimit, setDailyLimit] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quickAuthToken, setQuickAuthToken] = useState<string | null>(null);
  const [session, setSession] = useState<QuickAuthSession | null>(null);
  const abortRef = useRef(false);

  const resetState = useCallback(() => {
    setUser(null);
    setDbUser(null);
    setDailyLimit(null);
    setIsAuthenticated(false);
    setSession(null);
    setQuickAuthToken(null);
  }, []);

  const upsertSession = useCallback(async (token: string, contextUser: any): Promise<QuickAuthSession | null> => {
    try {
      const response = await fetch('/api/auth-quick', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          user: contextUser,
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body?.error ?? 'Quick auth failed');
      }

      const data = await response.json();
      return data.session as QuickAuthSession;
    } catch (err) {
      console.error('Failed to upsert quick auth session:', err);
      throw err;
    }
  }, []);

  const requestNotificationsIfNeeded = useCallback(
    async (token: string, currentSession: QuickAuthSession | null) => {
      if (!currentSession || currentSession.notifyEnabled) {
        return currentSession;
      }

      try {
        if (typeof sdk.actions?.requestNotifications === 'function') {
          await sdk.actions.requestNotifications();

          await fetch('/api/notifications-subscribe', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              notifyEnabled: true,
            }),
          });

          return {
            ...currentSession,
            notifyEnabled: true,
          };
        }

        console.log('Notifications API unavailable on this platform.');
        return currentSession;
      } catch (err) {
        console.warn('User did not enable notifications:', err);
        return currentSession;
      }
    },
    [],
  );

  const authenticate = useCallback(async () => {
    abortRef.current = false;
    setIsLoading(true);
    setError(null);

    try {
      const isMiniApp = await sdk.isInMiniApp();

      if (!isMiniApp) {
        if (allowBrowserAccess && AUTH_MODE !== 'quick') {
          console.warn('Running outside mini app with legacy auth mode - using mock user');
          if (abortRef.current) return false;
          setUser(MOCK_USER);
          setIsAuthenticated(true);
          setDbUser({ id: 'local-mock', fid: MOCK_USER.fid, isMock: true });
          setDailyLimit({
            canDoDaily: true,
            canDoAdditional: true,
            stats: {
              dailyUsed: 0,
              dailyLimit: 1,
              dailyRemaining: 1,
              additionalUsed: 0,
              maxAdditional: 2,
              additionalRemaining: 2,
            },
          });
          return true;
        }

        if (allowBrowserAccess) {
          console.warn('Not in mini app environment - falling back to mock auth for local dev');
          if (abortRef.current) return false;
          setUser(MOCK_USER);
          setIsAuthenticated(true);
          setDbUser({ id: 'local-mock', fid: MOCK_USER.fid, isMock: true });
          return true;
        }

        throw new Error('Not running inside a Farcaster Mini App');
      }

      const context = await sdk.context;
      if (!context.user) {
        throw new Error('Mini app context missing user');
      }

      const farcasterUser: FarcasterUser = {
        fid: context.user.fid,
        username: context.user.username || `fid_${context.user.fid}`,
        display_name: context.user.displayName || context.user.username || `User ${context.user.fid}`,
        pfp_url: context.user.pfpUrl || '',
        profile: { bio: { text: context.user.bio?.text ?? '' } },
        follower_count: context.user.followerCount ?? 0,
        following_count: context.user.followingCount ?? 0,
      };

      if (AUTH_MODE === 'legacy') {
        const legacyDbUser = await createOrUpdateUser(farcasterUser);
        if (abortRef.current) return false;

        setUser(farcasterUser);
        setIsAuthenticated(true);
        setDbUser(legacyDbUser);

        try {
          const limits = await checkReadingLimits(farcasterUser.fid);
          if (!abortRef.current) {
            setDailyLimit(limits);
          }
        } catch (limitError) {
          console.warn('Failed to fetch reading limits (legacy):', limitError);
        }

        return true;
      }

      const { token } = await sdk.quickAuth.getToken();
      if (abortRef.current) return false;
      setQuickAuthToken(token);

      const newSession = await upsertSession(token, context.user);
      let resolvedSession = newSession;

      try {
        resolvedSession = await requestNotificationsIfNeeded(token, newSession);
      } catch (notificationError) {
        console.warn('Notification request failed:', notificationError);
      }

      if (abortRef.current) return false;

      const dbUserResponse = await createOrUpdateUser(farcasterUser);
      if (abortRef.current) return false;

      setUser(farcasterUser);
      setIsAuthenticated(true);
      setDbUser(dbUserResponse);
      setSession(resolvedSession);

      try {
        const limits = await checkReadingLimits(farcasterUser.fid);
        if (!abortRef.current) {
          setDailyLimit(limits);
        }
      } catch (limitError) {
        console.warn('Failed to fetch reading limits:', limitError);
        if (!abortRef.current) {
          setDailyLimit(null);
        }
      }

      return true;
    } catch (err) {
      console.error('Mini app authentication failed:', err);
      if (!abortRef.current) {
        setError(err instanceof Error ? err.message : 'Authentication failed');
        resetState();
      }
      return false;
    } finally {
      if (!abortRef.current) {
        setIsLoading(false);
      }
    }
  }, [requestNotificationsIfNeeded, resetState, upsertSession, checkReadingLimits, createOrUpdateUser]);

  useEffect(() => {
    abortRef.current = false;
    authenticate();

    return () => {
      abortRef.current = true;
    };
  }, [authenticate]);

  const notifyEnabled = useMemo(() => session?.notifyEnabled ?? false, [session?.notifyEnabled]);

  return {
    user,
    dbUser,
    dailyLimit,
    isAuthenticated,
    isLoading,
    error,
    quickAuthToken,
    notifyEnabled,
    reauthenticate: authenticate,
  };
};
